/**
 * Builders for the unified changeset diff style (the frontend counterpart of
 * ChangeSet::Renderer's :diff style). A changeset is `{ attribute: [old, new] }` —
 * the ActiveRecord saved_changes shape that bulk import reports, version diffs, and
 * import previews all share. Values may be scalars, arrays (lists), or objects
 * (settings hashes / normalized association payloads).
 *
 * Display entries are `{ name, label, state }` where state is "kept", "added", or
 * "removed" — plus "changed" for a list item whose label survived but whose payload
 * differs; changed entries carry `details` of `{ key, values }` sub-diffs. Added labels
 * carry a literal "+" prefix when the value is a pure addition (so the change is not
 * conveyed by colour alone), and removed entries render struck through.
 */

function isBlank(value) {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  );
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Structural equality for payload comparison; data payloads are plain JSON shapes.
function sameValue(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function humanizeValue(value) {
  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

// Association lists arrive normalized as [{ label: attributes }] (the backend's
// ChangeSet::Utils.normalize_association shape) — the object key is the display label.
function listLabel(value) {
  if (isPlainObject(value)) {
    return Object.keys(value)[0] ?? "";
  }

  return humanizeValue(value);
}

// The payload of a normalized-association entry is the single-key object's value; any
// other element diffs as itself.
function payloadFor(value) {
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 1) {
      return value[keys[0]];
    }
  }

  return value;
}

// Only changed keys are emitted, each as `{ key, values }` where values are the
// scalar-style entries for that key's transition. One level of nesting is rendered;
// deeper structures compare (and display) as their JSON text.
export function hashDiffEntries(from, to) {
  const fromObject = isPlainObject(from) ? from : {};
  const toObject = isPlainObject(to) ? to : {};
  const keys = [...new Set([...Object.keys(fromObject), ...Object.keys(toObject)])];

  return keys
    .filter((key) => !sameValue(fromObject[key], toObject[key]))
    .map((key) => ({ key, values: scalarDiffEntries(fromObject[key], toObject[key]) }));
}

// Element-wise list diff: kept names plain and first, added names prefixed with "+",
// removed names appended last. An element whose label survives with a different payload
// (a normalized association whose attributes changed) becomes a "changed" entry carrying
// the payload's per-key sub-diffs. Each current element can satisfy only one resulting
// element (exact match first, then same-label payload change), so duplicate values keep
// their cardinality — leftovers in the pool are the removals.
export function listDiffEntries(current, resulting) {
  const pool = (current ?? []).map((value) => [listLabel(value), value]);

  const entries = (resulting ?? []).map((value) => {
    const label = listLabel(value);
    const exactIndex = pool.findIndex(([poolLabel, poolValue]) => poolLabel === label && sameValue(poolValue, value));

    if (exactIndex !== -1) {
      pool.splice(exactIndex, 1);
      return { name: label, label, state: "kept" };
    }

    const labelIndex = pool.findIndex(([poolLabel]) => poolLabel === label);
    if (labelIndex !== -1) {
      const [[, matchValue]] = pool.splice(labelIndex, 1);
      // JSON.stringify equality is key-order sensitive, so a reordered-but-equal payload
      // can land here with nothing actually changed — treat that as kept, not "Label ()".
      const details = hashDiffEntries(payloadFor(matchValue), payloadFor(value));
      if (details.length === 0) {
        return { name: label, label, state: "kept" };
      }

      return { name: label, label, state: "changed", details };
    }

    return { name: label, label: `+${label}`, state: "added" };
  });

  for (const [label] of pool) {
    entries.push({ name: label, label, state: "removed" });
  }

  return entries;
}

// Whole-value scalar diff: a change renders as the old value (removed) followed by the
// new one; a pure addition gets the "+" prefix; a cleared value renders as removed only.
export function scalarDiffEntries(from, to) {
  const fromBlank = isBlank(from);
  const toBlank = isBlank(to);

  if (fromBlank && toBlank) {
    return [];
  }

  const fromLabel = humanizeValue(from);
  const toLabel = humanizeValue(to);

  if (fromBlank) {
    return [{ name: toLabel, label: `+${toLabel}`, state: "added" }];
  }

  if (toBlank) {
    return [{ name: fromLabel, label: fromLabel, state: "removed" }];
  }

  return [
    { name: fromLabel, label: fromLabel, state: "removed" },
    { name: toLabel, label: toLabel, state: "added" },
  ];
}

// One row per changed attribute: `{ attribute, values }` — lists diffed element-wise,
// object transitions per changed key, scalars whole-value. Attributes whose transition
// produces no visible entries are dropped.
export function changesetAttributeEntries(changeset) {
  return Object.entries(changeset ?? {})
    .map(([attribute, transition]) => {
      const [from, to] = Array.isArray(transition) ? transition : [undefined, transition];

      let values;
      if (Array.isArray(from) || Array.isArray(to)) {
        values = listDiffEntries(Array.isArray(from) ? from : [], Array.isArray(to) ? to : []);
      } else if (isPlainObject(from) || isPlainObject(to)) {
        const details = hashDiffEntries(from, to);
        values = details.length > 0 ? [{ name: attribute, label: null, state: "changed", details }] : [];
      } else {
        values = scalarDiffEntries(from, to);
      }

      return { attribute, values };
    })
    .filter((entry) => entry.values.length > 0);
}
